import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import { makeStyles } from "@material-ui/core/styles";
import MuiTab from "@material-ui/core/Tab";
import FriendIcon from "@material-ui/icons/People";
import TabContext from "@material-ui/lab/TabContext";
import TabList from "@material-ui/lab/TabList";
import TabPanel from "@material-ui/lab/TabPanel";
import { RefObject, useEffect, useMemo, useState } from "react";
import {
    getFriends,
    getRequestedUsers,
    useRelatedUsers,
} from "../../../../../../../../common/hooks/useRelatedUsers";
import { AlertIcon } from "../../../../../../../../icon/AlertIcon";
import { RelatedUser, User } from "../../../../../../../shared/User/types";
import { InitialView } from "../../../MyPageTop/types";
import { OpenableCard } from "../OpenableCard";
import { FriendsTab } from "./components/FriendsTab/FriendsTab";
import { RequestsTab } from "./components/RequestsTab/RequestsTab";
import { FindFriendsButton } from "./FindFriendsButton";
import { Tab } from "./types";

export function FriendsCard({
    user,
    initialView,
    scrollableContainerRef,
}: {
    user: User;
    initialView?: InitialView;
    scrollableContainerRef: RefObject<HTMLDivElement>;
}) {
    const [open, setOpen] = useState(false);
    const { relatedUsers } = useRelatedUsers();
    const { friends, requestedUsers } = useMemo(
        () => ({
            ...getFriends(relatedUsers),
            ...getRequestedUsers(relatedUsers),
        }),
        [relatedUsers]
    );

    const [currentTab, setCurrentTab] = useState<Tab>(Tab.friends);
    useEffect(() => {
        if (!open) {
            return;
        }
        if (requestedUsers.length > 0) {
            setCurrentTab(Tab.requests);
        }
    }, [open, relatedUsers.length > 0]); // Check relatedUsers' length to detect the user's login

    const c = useStyles();

    return (
        <OpenableCard
            title={
                <span>
                    Friends:{" "}
                    <span className={c.titleNumber}>{friends.length}</span>
                </span>
            }
            icon={<FriendIcon />}
            saveKey="MyPageFriendsCard"
            id="MyPageFriendsCard"
            open={open}
            setOpen={setOpen}
            alwaysShowIcon
            alwaysShowTitle
            initiallyOpenedId={initialView}
            exclamationMark={requestedUsers.length > 0}
            scrollableContainerRef={scrollableContainerRef}
        >
            <TabsContainer
                requestedUsers={requestedUsers}
                friends={friends}
                user={user}
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
            />
        </OpenableCard>
    );
}
const useStyles = makeStyles(theme => ({
    titleNumber: { color: theme.palette.success.dark },
}));

function TabsContainer({
    requestedUsers,
    friends,
    user,
    currentTab,
    setCurrentTab,
}: {
    requestedUsers: RelatedUser[];
    friends: RelatedUser[];
    user: User;
    currentTab: Tab;
    setCurrentTab: (tab: Tab) => void;
}) {
    const c = useTabsContainerStyles();

    return (
        <Card className={c.container}>
            <Card>
                <TabContext value={currentTab}>
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <TabList
                            onChange={(ev, newTab: Tab) => {
                                setCurrentTab(newTab);
                            }}
                            aria-label="Friends"
                            centered
                            textColor="primary"
                            indicatorColor="primary"
                            variant="fullWidth"
                        >
                            <MuiTab label={"Friends"} value={Tab.friends} />
                            <MuiTab
                                label={
                                    requestedUsers.length > 0 ? (
                                        <RequestTabLabelWithAlert />
                                    ) : (
                                        "Requests"
                                    )
                                }
                                value={Tab.requests}
                            />
                        </TabList>
                    </Box>
                    <TabPanel value={Tab.friends} classes={{ root: c.tab }}>
                        <FriendsTab friends={friends} user={user} />
                    </TabPanel>
                    <TabPanel value={Tab.requests} classes={{ root: c.tab }}>
                        <RequestsTab
                            requestedUsers={requestedUsers}
                            user={user}
                            setCurrentTab={setCurrentTab}
                        />
                    </TabPanel>
                </TabContext>
            </Card>

            <FindFriendsButton noFriend={friends.length <= 0} />
        </Card>
    );
}
const useTabsContainerStyles = makeStyles(theme => ({
    container: {
        backgroundColor: theme.palette.grey[100],
        padding: 5,
        borderTopRightRadius: 0,
        borderTopLeftRadius: 0,
    },
    tab: { padding: 0 },
}));

function RequestTabLabelWithAlert() {
    const c = useRequestTabLabelStyles();
    return (
        <div className={c.redTitleContainer}>
            Requests
            <AlertIcon size={22} className={c.alertIcon} />
        </div>
    );
}
const useRequestTabLabelStyles = makeStyles(theme => ({
    redTitleContainer: {
        color: theme.palette.secondary.main,
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        position: "relative",
    },
    alertIcon: {
        position: "absolute",
        right: -25,
        top: -1.5,
    },
}));
